<template>
  <div class="page" id="corrupted-rows">
    <div class="mt-4 head-actions">
      <button class="px-3 btn btn-danger btn-sm" @click="stopProcessTheFile">
        <i class="fa fa-times"></i>
        {{ $t('auto-dialer.corrupted-rows.actions.stop') }}
      </button>
      <!-- ignore all -->
      <button
        class="px-3 btn btn-sm btn-warning"
        @click="ignoreAllCorruptedRows"
      >
        <i class="fa fa-trash"></i>
        {{ $t('auto-dialer.corrupted-rows.actions.ignore') }}
      </button>
    </div>

    <data-table
      ref="corruptedCustomersTable"
      :data="corruptedRows"
      :fields="translatedTableFields"
      :pagination="pagination"
      @changePage="onPageChange($event)"
      :loading="corruptedRowLoading"
    >
      <template v-slot:reasons="{ row }">
        <div class="reasons">
          <b-badge
            v-for="reason in row.reasons"
            :key="reason"
            variant="warning"
            class="m-2 white-space-pre-line"
          >
            {{ reason }}
          </b-badge>
        </div>
      </template>
      <template v-slot:actions="{ row }">
        <button class="px-3 btn btn-primary btn-sm" @click="toggleDetails(row)">
          <i
            :class="
              $refs.corruptedCustomersTable.activeRowDetailsId === row.id
                ? 'fa fa-eye-slash'
                : 'fa fa-eye'
            "
          ></i>
        </button>
      </template>
      <template v-slot:details="{ row }">
        <div class="details-form">
          <div class="row">
            <Field
              class="col-12 col-lg-6"
              :label="$t('auto-dialer.create.step2.manually.form.name.label')"
              :placeholder="
                $t('auto-dialer.create.step2.manually.form.name.placeholder')
              "
              v-model="corruptedRowModel.name"
              :error="corruptedRowErrors[`name`]"
            />

            <Field
              class="col-12 col-lg-6"
              :label="$t('auto-dialer.create.step2.manually.form.phone.label')"
              :error="corruptedRowErrors[`phone`]"
            >
              <b-form-input
                class="border"
                :placeholder="
                  $t('auto-dialer.create.step2.manually.form.phone.placeholder')
                "
                v-model="corruptedRowModel.phone"
                type="number"
                min="1"
                @keydown="onlyAcceptNumbers"
              />
            </Field>
          </div>

          <!-- info text area -->
          <Field
            :label="$t('auto-dialer.create.step2.manually.form.info.label')"
            :error="corruptedRowErrors[`info`]"
          >
            <b-form-textarea
              class="border"
              :placeholder="
                $t('auto-dialer.create.step2.manually.form.info.placeholder')
              "
              v-model="corruptedRowModel.information"
            />
          </Field>

          <div class="">
            <button
              class="btn btn-primary btn-sm"
              @click="fixCorruptedRow(row)"
            >
              {{ $t('auto-dialer.corrupted-rows.form.fix-and-save') }}
            </button>
          </div>
        </div>
      </template>
    </data-table>
  </div>
</template>

<script>
import AutoDialerService from '../../../services/autoDialer.service'
import DataTable from '../../../elements/Table.vue'
import Field from '../../../elements/Field.vue'
import { onlyAcceptNumbers } from '../../../utils/utils'
import { isValidPhoneNumber } from '../../../utils/phone'
import { yupToKV } from '../../../utils/yup'
import { object, string } from 'yup'

export default {
  name: 'CorruptedRows',
  components: {
    DataTable,
    Field
  },
  data() {
    return {
      corruptedRowLoading: false,
      fields: [
        {
          accessor: 'status',
          header: 'auto-dialer.corrupted-rows.table.status'
        },
        {
          accessor: 'reasons',
          header: 'auto-dialer.corrupted-rows.table.reasons'
        },
        {
          accessor: 'actions',
          header: 'auto-dialer.corrupted-rows.table.actions'
        }
      ],
      corruptedRows: [],
      pagination: {
        totalPages: 0,
        page: 1
      },
      corruptedRowModel: {
        name: '',
        phone: '',
        information: ''
      },
      corruptedRowErrors: {}
    }
  },
  methods: {
    onlyAcceptNumbers(event) {
      onlyAcceptNumbers(event)
    },
    onPageChange(page) {
      this.pagination.page = page
      this.fetchCorruptedRows(page)
    },
    async fetchCorruptedRows(page) {
      try {
        this.corruptedRowLoading = true
        const {
          corruptedRows,
          ...pagination
        } = await AutoDialerService.fetchCorruptedRows(
          this.$route.params.id,
          page
        )

        this.corruptedRows = corruptedRows
        this.pagination.totalPages = pagination.totalPages
      } catch (error) {
        console.error(error)
      } finally {
        this.corruptedRowLoading = false
      }
    },
    toggleDetails(row) {
      if (!row.row) return
      this.corruptedRowErrors = {}
      this.corruptedRowModel = row.row

      this.$refs.corruptedCustomersTable.toggleDetails(row.id)
    },
    async fixCorruptedRow(row) {
      try {
        await this.schema
          .validate(this.corruptedRowModel, { abortEarly: false })
          .catch(err => {
            this.corruptedRowErrors = yupToKV(err)
          })

        if (!this.schema.isValidSync(this.corruptedRowModel)) {
          return
        }

        await AutoDialerService.fixCorruptedRow(
          this.$route.params.id,
          row.id,
          this.corruptedRowModel
        )
        this.toast(
          this.$t('auto-dialer.corrupted-rows.toasts.corrupted-row-fixed'),
          { type: 'success' }
        )

        if (this.corruptedRows.length === 1) {
          this.$router.push({
            name: 'auto-dialer'
          })
        }
        this.fetchCorruptedRows()
      } catch (error) {
        const errorMessage = error.response
          ? error.response.data.message
          : error.message
        this.toast(errorMessage, { type: 'error' })
      } finally {
      }
    },
    async ignoreAllCorruptedRows() {
      try {
        await AutoDialerService.ignoreAllCorruptedRows(this.$route.params.id)
        this.toast(
          this.$t('auto-dialer.corrupted-rows.toasts.corrupted-rows-ignored')
        )
        this.$router.push({
          name: 'auto-dialer'
        })
      } catch (error) {
        const errorMessage = error.response
          ? error.response.data.message
          : error.message
        this.toast(errorMessage, { type: 'error' })
      }
    },
    async stopProcessTheFile() {
      try {
        await AutoDialerService.stopProcessing(this.$route.params.id)
        this.toast(
          this.$t('auto-dialer.corrupted-rows.toasts.file-processing-stopped')
        )
        this.$router.push({
          name: 'auto-dialer'
        })
      } catch (error) {
        const errorMessage = error.response
          ? error.response.data.message
          : error.message
        this.toast(errorMessage, { type: 'error' })
      }
    }
  },
  computed: {
    translatedTableFields() {
      return this.fields.map(field => {
        return {
          ...field,
          header: this.$t(field.header)
        }
      })
    },
    schema() {
      return object().shape({
        name: string()
          .trim()
          .required(
            this.$t('auto-dialer.create.step2.manually.form.name.required')
          ),
        phone: string()
          .trim()
          .test(
            'isValidPhone',
            this.$t('auto-dialer.create.step2.manually.form.phone.invalid'),
            isValidPhoneNumber
          )
          .required(
            this.$t('auto-dialer.create.step2.manually.form.phone.required')
          ),
        information: string().nullable()
      })
    }
  },
  mounted() {
    this.fetchCorruptedRows()
  }
}
</script>

<style scoped>
.details-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.head-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.white-space-pre-line {
  white-space: pre-line;
}
</style>
